import type { Player } from "../types.js";
import {
  HALF_ROUND,
  allocateStrokes,
  courseHandicap,
  holesInPlay,
} from "../handicap.js";
import type { Format, FormatScoreInput, RoundResult } from "./types.js";
import { buildScoreMap, holeCount, scoreSegmentsByTotal, teamGrossOnHole, holeNumbersInPlay, describeSegmentPoints } from "./util.js";

interface ScrambleConfig {
  lowerPercent?: number;
  higherPercent?: number;
}

function teamKey(team: string[]): string {
  return [...team].sort().join(",");
}

/**
 * The team's one ball, net of its combined handicap. Shared by `score` and the side
 * breakdown so the grid can't disagree with the points about what a side shot.
 */
function teamValue({ round, course, players, scores }: FormatScoreInput) {
  const config = (round.formatConfig ?? {}) as ScrambleConfig;
  const lowerPercent = config.lowerPercent ?? 35;
  const higherPercent = config.higherPercent ?? 15;

  const scoreMap = buildScoreMap(scores);
  const playerById = new Map(players.map((p) => [p.id, p]));
  const holes = holesInPlay(course.holes, round.holeSet);

  // Combined team handicap (and therefore strokes per hole) depends on which two
  // players are paired, which changes per segment in a partner-swap scramble — so
  // it's computed lazily per distinct team rather than once for the whole round.
  const strokesCache = new Map<string, Map<number, number>>();
  const strokesForTeam = (team: string[]): Map<number, number> => {
    const key = teamKey(team);
    const cached = strokesCache.get(key);
    if (cached) return cached;

    const chs = team
      .map((id) => playerById.get(id))
      .filter((p): p is Player => !!p)
      .map((p) => courseHandicap(p.handicapIndex, course.slope));
    // Math.min/max of an empty array is +/-Infinity, which would make combined NaN
    // below (e.g. a team referencing a player removed from the roster) — fall back
    // to no handicap allowance rather than corrupting every hole's strokes with NaN.
    const lower = chs.length ? Math.min(...chs) : 0;
    const higher = chs.length ? Math.max(...chs) : 0;
    const combined = lower * (lowerPercent / 100) + higher * (higherPercent / 100);
    // Halved for a nine, on the same rule as every other format's allowance — and, like
    // them, decided by how many holes are being played rather than by what the round
    // asked for, so a nine-hole course gets half whatever its holeSet says.
    const teamPlayingHcp = Math.round(
      holes.length > HALF_ROUND ? combined : combined / 2,
    );

    const strokes = allocateStrokes(teamPlayingHcp, holes);
    strokesCache.set(key, strokes);
    return strokes;
  };

  return (team: string[], hole: number): number | null => {
    const gross = teamGrossOnHole(scoreMap, team, hole);
    if (gross == null) return null;
    const received = strokesForTeam(team).get(hole) ?? 0;
    return gross - received;
  };
}

export const scramble: Format = {
  id: "scramble",
  name: "Scramble",
  scope: "team",
  uses: { teamPoints: true },
  entry: "perTeam",
  configSchema: {
    type: "object",
    properties: {
      lowerPercent: {
        type: "number",
        default: 35,
        title: "% of the lower handicap",
        description: "Team handicap = this share of the better player's handicap, plus the share below of the other's.",
      },
      higherPercent: { type: "number", default: 15, title: "% of the higher handicap" },
    },
  },
  perHoleSchema: null,

  score(input): RoundResult {
    const { round, course, players, onlyHoles } = input;
    return scoreSegmentsByTotal(
      round,
      players,
      holeNumbersInPlay(round, course),
      teamValue(input),
      onlyHoles,
    );
  },

  sideValueOnHole: teamValue,

  describeRules(round, _players, course) {
    const config = (round.formatConfig ?? {}) as ScrambleConfig;
    const lowerPercent = config.lowerPercent ?? 35;
    const higherPercent = config.higherPercent ?? 15;
    return [
      `Scramble across ${holeCount(round, course)} holes: all players tee off, the team plays the best shot, repeat until holed. One score per team per hole.`,
      `Team handicap: ${lowerPercent}% of the lower handicap + ${higherPercent}% of the higher, recalculated per segment since partners change.`,
      describeSegmentPoints(round),
    ];
  },
};
